import { useEffect, useState } from 'react';
import {
  Paper, Typography, TextField, Button, Stack, Alert, Snackbar, MenuItem, Divider,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { officerNav } from '../components/officerNav';
import api from '../services/api';

// Award document types (OCDS awards/contracts documents).
const DOC_LABEL = {
  AWARD_LETTER: 'Award letter',
  SIGNED_CONTRACT: 'Signed contract',
  EVALUATION_REPORT: 'Evaluation report',
  OTHER: 'Other',
};


export default function ContractAward() {
  const { tenderId } = useParams();
  const [bids, setBids] = useState([]);
  const [award, setAward] = useState(null);
  const [form, setForm] = useState({ winningBidId: '', vendorName: '', contractAmount: '', justification: '', awardDate: '', contractStart: '', contractEnd: '', contractStatus: '', dateSigned: '' });
  const [docType, setDocType] = useState('AWARD_LETTER');
  const [docFile, setDocFile] = useState(null);
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    // Bids (to choose the winner) + any existing award.
    const [bidsRes, awardRes] = await Promise.all([
      api.get(`/tenders/${tenderId}/bids`),
      api.get(`/tenders/${tenderId}/award`),
    ]);
    setBids(bidsRes.data.data.bids || []);
    if (awardRes.data.data) {
      setAward(awardRes.data.data);
      const a = awardRes.data.data;
      const d = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');
      setForm({
        winningBidId: a.winningBidId || '', vendorName: a.vendorName || '',
        contractAmount: a.contractAmount || '', justification: a.justification || '',
        awardDate: d(a.awardDate), contractStart: d(a.contractStart),
        contractEnd: d(a.contractEnd), contractStatus: a.contractStatus || '', dateSigned: d(a.dateSigned),
      });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch((e) => setToast(e.response?.data?.error?.message || 'Could not load')); }, [tenderId]);

  const pickWinner = (bidId) => {
    const b = bids.find((x) => x.id === bidId);
    setForm((f) => ({ ...f, winningBidId: bidId, vendorName: b ? b.vendorName : f.vendorName }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('winningBidId', form.winningBidId);
      fd.append('vendorName', form.vendorName);
      fd.append('contractAmount', form.contractAmount);
      fd.append('justification', form.justification);
      fd.append('awardDate', form.awardDate);
      fd.append('contractStart', form.contractStart);
      fd.append('contractEnd', form.contractEnd);
      fd.append('contractStatus', form.contractStatus);
      fd.append('dateSigned', form.dateSigned);
      await api.post(`/tenders/${tenderId}/award`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setToast('Award saved');
      load();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  const saveDates = async () => {
    setBusy(true);
    try {
      await api.patch(`/tenders/${tenderId}/award/dates`, {
        awardDate: form.awardDate || null,
        contractStart: form.contractStart || null,
        contractEnd: form.contractEnd || null,
        contractStatus: form.contractStatus || null,
        dateSigned: form.dateSigned || null,
      });
      setToast('Contract dates updated');
      load();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Could not update dates'); }
    finally { setBusy(false); }
  };

  const uploadDoc = async () => {
    if (!docFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('docType', docType);
      fd.append('file', docFile);
      await api.post(`/tenders/${tenderId}/award/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocFile(null);
      setToast('Document uploaded');
      load();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Upload failed'); }
    finally { setBusy(false); }
  };

  // Documents come through the API (it needs the login token) and are then
  // shown or saved from a temporary browser link. The tab for View is opened
  // at the click itself so pop-up blockers allow it.
  const openDoc = async (d, download) => {
    const tab = download ? null : window.open('', '_blank');
    try {
      const res = await api.get(`/award-documents/${d.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = d.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (tab) {
        tab.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (tab) tab.close();
      setToast('Could not open the document');
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Remove ${d.fileName}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/award-documents/${d.id}`);
      setToast('Document removed');
      load();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Could not remove the document'); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!window.confirm('Publish this award? It will become visible on the public portal.')) return;
    try {
      await api.post(`/tenders/${tenderId}/award/publish`, form.awardDate ? { awardDate: form.awardDate } : {});
      setToast('Award published');
      load();
    } catch (e) { setToast(e.response?.data?.error?.message || 'Publish failed'); }
  };

  return (
    <DashboardLayout nav={officerNav} title="Contract Award">
      {award?.published && <Alert severity="success" sx={{ mb: 2 }}>Published to the public portal on {new Date(award.publishedAt).toLocaleString('en-GB')}. Contract dates below can still be corrected.</Alert>}

      <Paper sx={{ p: 3, maxWidth: 760 }}>
        <Typography variant="subtitle1" gutterBottom>Winning Vendor</Typography>
        <TextField select label="Select from bidders" value={form.winningBidId} onChange={(e) => pickWinner(e.target.value)} fullWidth sx={{ mb: 2 }}>
          {bids.map((b) => <MenuItem key={b.id} value={b.id}>{b.vendorName} ({b.reference})</MenuItem>)}
        </TextField>
        <TextField label="Winning vendor name" value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })} fullWidth sx={{ mb: 2 }} />
        <TextField label="Contract Amount (₦)" type="number" value={form.contractAmount} onChange={(e) => setForm({ ...form, contractAmount: e.target.value })} fullWidth sx={{ mb: 2 }} />
        <TextField label="Justification" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} fullWidth multiline minRows={3} sx={{ mb: 2 }} />


        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>Contract Dates</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          The real-world dates from the contract documents. These are separate from the date the award is published on this portal, and may be backdated for historical records.
        </Typography>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <TextField type="date" label="Award date" size="small" InputLabelProps={{ shrink: true }} value={form.awardDate} onChange={(e) => setForm({ ...form, awardDate: e.target.value })} sx={{ minWidth: 190 }} />
          <TextField type="date" label="Date signed" size="small" InputLabelProps={{ shrink: true }} value={form.dateSigned} onChange={(e) => setForm({ ...form, dateSigned: e.target.value })} sx={{ minWidth: 190 }} />
          <TextField type="date" label="Contract start" size="small" InputLabelProps={{ shrink: true }} value={form.contractStart} onChange={(e) => setForm({ ...form, contractStart: e.target.value })} sx={{ minWidth: 190 }} />
          <TextField type="date" label="Contract end" size="small" InputLabelProps={{ shrink: true }} value={form.contractEnd} onChange={(e) => setForm({ ...form, contractEnd: e.target.value })} sx={{ minWidth: 190 }} />
        </Stack>
        <TextField select label="Contract status" size="small" value={form.contractStatus} onChange={(e) => setForm({ ...form, contractStatus: e.target.value })} sx={{ minWidth: 240, mb: 2 }}
          helperText="'Completed or ended' covers both normal completion and early termination.">
          <MenuItem value="">Not recorded</MenuItem>
          <MenuItem value="PENDING">Not started</MenuItem>
          <MenuItem value="ACTIVE">In progress</MenuItem>
          <MenuItem value="TERMINATED">Completed or ended</MenuItem>
          <MenuItem value="CANCELLED">Cancelled before it started</MenuItem>
        </TextField>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={save} disabled={busy || award?.published}>Save Award</Button>
          {award?.published
            ? <Button variant="contained" onClick={saveDates} disabled={busy}>Save Contract Dates</Button>
            : <Button variant="contained" onClick={publish} disabled={busy}>Publish Award</Button>}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 760, mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Award documents</Typography>
        {!award ? (
          <Typography variant="body2" color="text.secondary">Save the award first, then attach its documents here.</Typography>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Award letter, signed contract, evaluation report or other documents. Documents can be added after
              publication, since the signed contract often arrives later; they can be removed only while the award
              is unpublished.
            </Typography>
            {(award.documents || []).length === 0 && (
              <Typography variant="body2" sx={{ mb: 2 }}>No documents yet.</Typography>
            )}
            {(award.documents || []).map((d) => (
              <Stack key={d.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  <strong>{DOC_LABEL[d.docType] || d.docType}</strong>
                  {` - ${d.fileName} `}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {`(${new Date(d.uploadedAt).toLocaleDateString('en-GB')})`}
                  </Typography>
                </Typography>
                <Button size="small" onClick={() => openDoc(d, false)}>View</Button>
                <Button size="small" onClick={() => openDoc(d, true)}>Download</Button>
                {!award.published && (
                  <Button size="small" color="error" onClick={() => removeDoc(d)} disabled={busy}>Remove</Button>
                )}
              </Stack>
            ))}
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
              <TextField select label="Document type" size="small" value={docType}
                onChange={(e) => setDocType(e.target.value)} sx={{ minWidth: 200 }}>
                {Object.entries(DOC_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </TextField>
              <Button component="label" variant="outlined">
                {docFile ? docFile.name : 'Choose file'}
                <input type="file" hidden accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={(e) => setDocFile(e.target.files[0] || null)} />
              </Button>
              <Button variant="contained" onClick={uploadDoc} disabled={busy || !docFile}>Upload</Button>
            </Stack>
          </>
        )}
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')}>
        <Alert severity="info" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
